import {
	ACCESS_PROOF_LABELS,
	normalizeAccessProofOptions,
	type AccessProofInputModel,
	type AccessProofKind,
	type FormAccessProofOptionsModel,
	type FormFieldDefinitionModel,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { Button } from '../Button';
import { Input } from '../Input';

/** What the page's Send code call answers: the message to show, and whether it went out. */
export interface FormAccessCodeSendResultModel {
	ok: boolean;
	message: string;
}

export type FormAccessCodeSenderModel = (
	channel: 'email' | 'phone',
) => Promise<FormAccessCodeSendResultModel>;

interface FormAccessProofQuestionProps {
	field: Pick<
		FormFieldDefinitionModel,
		'fieldKey' | 'label' | 'helpText' | 'fieldOptions'
	>;
	value: AccessProofInputModel;
	onChange: (value: AccessProofInputModel) => void;
	/**
	 * Sends a code to the claimed customer's email or phone on file. Absent in the settings
	 * preview, where Send code is shown but does nothing.
	 */
	onSendCode?: FormAccessCodeSenderModel;
	/** The customer number typed so far; Send code needs one. */
	customerNumber: string;
	idPrefix: string;
	disabled?: boolean;
}

/** Seconds before Send code can be pressed again for the same channel. */
const RESEND_SECONDS = 30;

/**
 * PD-17590 — an access request's proof block: the proofs the form offers, each as its own
 * small question, and a Send code button for the email and phone codes. The values are the
 * page's own (sent in the submit body's `accessProof`, never as answers), so this is a
 * controlled component the page holds the value of. Every proof is optional: a request with
 * fewer proofs than required still reaches the team, who confirm it themselves.
 */
export function FormAccessProofQuestion({
	field,
	value,
	onChange,
	onSendCode,
	customerNumber,
	idPrefix,
	disabled = false,
}: FormAccessProofQuestionProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const options: FormAccessProofOptionsModel = normalizeAccessProofOptions(
		field.fieldOptions?.accessProof ?? null,
	) ?? {
		customerNumberFieldKey: '',
		proofs: [],
		required: 1,
		totalsCount: 1,
		lookbackDays: 90,
		verifyWithProofs: true,
	};
	const [sending, setSending] = useState<'email' | 'phone' | null>(null);
	const [sent, setSent] = useState<
		Partial<Record<'email' | 'phone', FormAccessCodeSendResultModel>>
	>({});
	const [cooldown, setCooldown] = useState<
		Partial<Record<'email' | 'phone', number>>
	>({});

	// One tick a second while a channel waits to be sent again.
	useEffect(() => {
		if (Object.values(cooldown).every((seconds) => (seconds ?? 0) <= 0)) {
			return undefined;
		}
		const timer = window.setTimeout(() => {
			setCooldown((previous) => {
				const next: Partial<Record<'email' | 'phone', number>> = {};
				for (const [channel, seconds] of Object.entries(previous)) {
					next[channel as 'email' | 'phone'] = Math.max(0, (seconds ?? 0) - 1);
				}
				return next;
			});
		}, 1000);
		return (): void => {
			window.clearTimeout(timer);
		};
	}, [cooldown]);

	const id = (part: string): string => `${idPrefix}-${field.fieldKey}-${part}`;
	const set = (patch: Partial<AccessProofInputModel>): void => {
		onChange({ ...value, ...patch });
	};

	const sendCode = async (channel: 'email' | 'phone'): Promise<void> => {
		if (onSendCode == null || sending != null) {
			return;
		}
		setSending(channel);
		try {
			const result = await onSendCode(channel);
			setSent((previous) => ({ ...previous, [channel]: result }));
			if (result.ok) {
				setCooldown((previous) => ({ ...previous, [channel]: RESEND_SECONDS }));
			}
		} catch {
			setSent((previous) => ({
				...previous,
				[channel]: {
					ok: false,
					message:
						'We could not send the code. Please check your connection and try again.',
				},
			}));
		} finally {
			setSending(null);
		}
	};

	const codeProof = (
		kind: 'email_code' | 'phone_code',
	): React.ReactElement => {
		const channel = kind === 'email_code' ? 'email' : 'phone';
		const key = kind === 'email_code' ? 'emailCode' : 'phoneCode';
		const result = sent[channel];
		const wait = cooldown[channel] ?? 0;
		const noNumber = customerNumber.trim() === '';
		return (
			<div className="space-y-1">
				<label htmlFor={id(kind)} className="block text-sm font-medium text-default">
					{t(ACCESS_PROOF_LABELS[kind])}
				</label>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="shrink-0"
						disabled={disabled || noNumber || sending != null || wait > 0 || onSendCode == null}
						loading={sending === channel}
						onClick={() => {
							void sendCode(channel);
						}}
					>
						{wait > 0
							? t('Send again in {{seconds}}s', { seconds: wait })
							: result != null
								? t('Send another code')
								: t('Send code')}
					</Button>
					<Input
						id={id(kind)}
						inputMode="numeric"
						autoComplete="one-time-code"
						maxLength={7}
						placeholder={t('6-digit code')}
						value={value[key] ?? ''}
						disabled={disabled}
						onChange={(event) => {
							const text = event.target.value;
							set({ [key]: text.trim() === '' ? undefined : text });
						}}
					/>
				</div>
				<p className={cn('text-xs', colors.classes.textSecondary)}>
					{noNumber
						? t('Enter your customer number first.')
						: kind === 'email_code'
							? t('We send the code to the email address we have on file for your account.')
							: t('We text or call the phone number we have on file for your account.')}
				</p>
				{result != null && (
					<p
						role="status"
						className={cn(
							'text-xs',
							result.ok ? colors.classes.textSecondary : colors.classes.textDanger,
						)}
					>
						{t(result.message)}
					</p>
				)}
			</div>
		);
	};

	const totalsProof = (
		kind: 'invoice_totals' | 'order_totals',
	): React.ReactElement => {
		const key = kind === 'invoice_totals' ? 'invoiceTotals' : 'orderTotals';
		const amounts = value[key] ?? [];
		const isInvoice = kind === 'invoice_totals';
		const label =
			options.totalsCount === 2
				? isInvoice
					? t('The totals of two recent invoices')
					: t('The totals of two recent orders')
				: isInvoice
					? t('The total of a recent invoice')
					: t('The total of a recent order');
		return (
			<div className="space-y-1">
				<p className="text-sm font-medium text-default">{label}</p>
				<div className={cn('grid gap-2', options.totalsCount === 2 && 'sm:grid-cols-2')}>
					{Array.from({ length: options.totalsCount }, (_, index) => (
						<Input
							key={`${kind}-${index}`}
							id={id(`${kind}-${index}`)}
							aria-label={
								options.totalsCount === 2
									? isInvoice
										? t('Invoice total {{number}}', { number: index + 1 })
										: t('Order total {{number}}', { number: index + 1 })
									: label
							}
							inputMode="decimal"
							placeholder={t('0.00')}
							value={amounts[index] ?? ''}
							disabled={disabled}
							onChange={(event) => {
								const next = Array.from(
									{ length: options.totalsCount },
									(__, position) =>
										position === index ? event.target.value : (amounts[position] ?? ''),
								);
								set({
									[key]: next.every((amount) => amount.trim() === '')
										? undefined
										: next,
								});
							}}
						/>
					))}
				</div>
				<p className={cn('text-xs', colors.classes.textSecondary)}>
					{options.totalsCount === 2
						? t('Two different ones from the last {{days}} days, each to the cent.', {
								days: options.lookbackDays,
							})
						: t('From the last {{days}} days, to the cent.', {
								days: options.lookbackDays,
							})}
				</p>
			</div>
		);
	};

	const textProof = (
		kind: 'invoice_number' | 'order_number' | 'post_code',
	): React.ReactElement => {
		const key =
			kind === 'invoice_number'
				? 'invoiceNumber'
				: kind === 'order_number'
					? 'orderNumber'
					: 'postCode';
		return (
			<div className="space-y-1">
				<label htmlFor={id(kind)} className="block text-sm font-medium text-default">
					{t(ACCESS_PROOF_LABELS[kind])}
				</label>
				<Input
					id={id(kind)}
					inputMode={kind === 'post_code' ? 'numeric' : undefined}
					autoComplete={kind === 'post_code' ? 'postal-code' : 'off'}
					maxLength={64}
					value={value[key] ?? ''}
					disabled={disabled}
					onChange={(event) => {
						const text = event.target.value;
						set({ [key]: text.trim() === '' ? undefined : text });
					}}
				/>
			</div>
		);
	};

	const proof = (kind: AccessProofKind): React.ReactElement => {
		switch (kind) {
			case 'email_code':
			case 'phone_code':
				return codeProof(kind);
			case 'invoice_totals':
			case 'order_totals':
				return totalsProof(kind);
			default:
				return textProof(kind);
		}
	};

	// Create logins with proof off: nothing to prove, only what happens next.
	if (!options.verifyWithProofs || options.proofs.length === 0) {
		return (
			<p className={cn('text-sm', colors.classes.textSecondary)}>
				{t('Our team will confirm your account with you before setting up your login.')}
			</p>
		);
	}

	return (
		<div
			role="group"
			aria-labelledby={id('label')}
			className="space-y-4 rounded-md border border-default bg-surface px-4 py-3"
		>
			<div className="space-y-1">
				<p id={id('label')} className="text-sm font-semibold text-default">
					{t(field.label)}
				</p>
				<p className={cn('text-sm', colors.classes.textSecondary)}>
					{options.required === 2
						? t('Give two of these to get your login right away. Without them, our team confirms your account with you first.')
						: t('Give one of these to get your login right away. Without it, our team confirms your account with you first.')}
				</p>
				{field.helpText != null && (
					<p className={cn('text-xs', colors.classes.textSecondary)}>
						{t(field.helpText)}
					</p>
				)}
			</div>
			{options.proofs.map((kind) => (
				<div key={kind}>{proof(kind)}</div>
			))}
		</div>
	);
}

/**
 * The proof block as the renderer draws it on its own (the settings Preview): the visitor's
 * proofs are kept here, never sent, and Send code is shown but does nothing. A page that
 * submits (the storefront) draws FormAccessProofQuestion itself through renderCustomField,
 * holding the proofs and sending codes.
 */
export function FormAccessProofPreview({
	field,
	values,
	idPrefix,
	disabled = false,
}: {
	field: FormAccessProofQuestionProps['field'];
	values: Readonly<Record<string, unknown>>;
	idPrefix: string;
	disabled?: boolean;
}): React.ReactElement {
	const [proof, setProof] = useState<AccessProofInputModel>({});
	const numberKey =
		normalizeAccessProofOptions(field.fieldOptions?.accessProof ?? null)
			?.customerNumberFieldKey ?? '';
	const number = values[numberKey];
	return (
		<FormAccessProofQuestion
			field={field}
			value={proof}
			onChange={setProof}
			customerNumber={typeof number === 'string' ? number : ''}
			idPrefix={idPrefix}
			disabled={disabled}
		/>
	);
}
